import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { DOC_TYPE_LABELS, DOC_TYPE_COLORS, REQUIRED_DOC_TYPES, formatApiError, withAutoRetry } from "@/lib/api";
import { isFolderSupported, getSavedIds, getFolderName, saveBlobToFolder, markDocSaved, recordHiccupRecovery, getHiccupsSince } from "@/lib/localFolder";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, CheckCircle, WarningCircle, Package, ArrowRight, ClockCountdown, FloppyDisk, DownloadSimple, X } from "@phosphor-icons/react";

function StatCard({ label, value, hint, tone = "default", testid }) {
  const tones = {
    default: "text-zinc-950",
    success: "text-emerald-700",
    warn: "text-[#FF4F00]",
  };
  return (
    <div data-testid={testid} className="bg-white border border-zinc-200 p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</div>
      <div className={`mt-3 font-display font-black text-4xl tracking-tight ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [incomplete, setIncomplete] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [folderName, setFolderName] = useState(null);
  const [batch, setBatch] = useState(null); // {done, total, current: {awb, filename}, errors, running}
  const [hiccups7d, setHiccups7d] = useState(0);
  const [loading, setLoading] = useState(true);

  const recomputeOverdue = async (completeShips) => {
    const allDocIds = completeShips.flatMap((sh) => sh.document_ids || []);
    const savedMap = allDocIds.length ? await getSavedIds(allDocIds) : {};
    const cutoff = Date.now() - 7 * 86400000;
    return completeShips
      .map((sh) => {
        const eff = sh.doc_date || (sh.created_at ? sh.created_at.slice(0, 10) : "");
        const t = eff ? new Date(eff).getTime() : NaN;
        const ids = sh.document_ids || [];
        const savedCount = ids.filter((id) => savedMap[id]).length;
        const unsavedIds = ids.filter((id) => !savedMap[id]);
        const isOverdue = eff && !Number.isNaN(t) && t < cutoff && ids.length > 0 && savedCount < ids.length;
        const days = eff ? Math.max(0, Math.floor((Date.now() - t) / 86400000)) : 0;
        return { ...sh, _effective: eff, _days: days, _saved: savedCount, _total: ids.length, _unsavedIds: unsavedIds, _overdue: isOverdue };
      })
      .filter((x) => x._overdue)
      .sort((a, b) => a._effective.localeCompare(b._effective));
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, r, inc, cmpl] = await Promise.all([
          api.get("/shipments/stats"),
          api.get("/shipments", { params: { limit: 5 } }),
          api.get("/shipments", { params: { status: "incomplete", limit: 100 } }),
          isFolderSupported()
            ? api.get("/shipments", { params: { status: "complete", limit: 500 } })
            : Promise.resolve({ data: [] }),
        ]);
        setStats(s.data);
        setRecent(r.data.slice(0, 5));
        setIncomplete(inc.data);

        if (isFolderSupported()) {
          const fn = await getFolderName();
          setFolderName(fn);
          const overdueList = await recomputeOverdue(cmpl.data || []);
          setOverdue(overdueList);

          if (overdueList.length > 0 && fn) {
            const key = "overdue-warned-at";
            const last = Number(sessionStorage.getItem(key) || 0);
            if (Date.now() - last > 60 * 60 * 1000) {
              toast.warning(`${overdueList.length} shipment${overdueList.length === 1 ? "" : "s"} overdue for local backup (>7 days)`, {
                duration: 6000,
              });
              sessionStorage.setItem(key, String(Date.now()));
            }
          }
        }
        // Hiccup recovery count (last 7 days) — works even without folder
        try {
          const c = await getHiccupsSince(Date.now() - 7 * 86400000);
          setHiccups7d(c);
        } catch {}
      } finally { setLoading(false); }
    })();
  }, []);



  // Sentinel removed
  const saveAllOverdue = async () => {
    if (!isFolderSupported() || !folderName) {
      toast.error("Pick a folder first in Settings → Save to local folder");
      return;
    }
    // Fetch full doc metadata for each overdue shipment to get filename & id pairs
    setBatch({ done: 0, total: 0, current: null, errors: [], running: true });
    let allTasks = []; // [{shipId, awb, docId}]
    try {
      for (const s of overdue) {
        const { data } = await api.get(`/shipments/${s.id}`);
        for (const d of data.documents) {
          if (s._unsavedIds.includes(d.id)) {
            allTasks.push({ shipId: s.id, awb: s.awb_number, doc: d });
          }
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBatch(null);
      return;
    }

    if (allTasks.length === 0) {
      toast.info("Nothing to save — all overdue backups already up to date");
      setBatch(null);
      return;
    }

    const total = allTasks.length;
    setBatch({ done: 0, total, current: null, errors: [], running: true });
    let done = 0;
    const errors = [];
    for (const task of allTasks) {
      setBatch({ done, total, current: { awb: task.awb, filename: task.doc.original_filename }, errors, running: true });
      try {
        const { data } = await withAutoRetry(
          () => api.get(`/documents/${task.doc.id}/download`, { responseType: "blob" }),
          {
            onRetry: ({ attempt }) => setBatch((b) => b && ({ ...b, current: { ...b.current, retrying: attempt } })),
            onRecovered: recordHiccupRecovery,
          },
        );
        await saveBlobToFolder(task.doc.original_filename, data, task.awb);
        await markDocSaved(task.doc.id, { awb: task.awb });
        done += 1;
      } catch (e) {
        errors.push({
          shipId: task.shipId, awb: task.awb, docId: task.doc.id,
          filename: task.doc.original_filename, error: e.message || formatApiError(e),
        });
      }
    }
    setBatch({ done, total, current: null, errors, running: false });

    // Refresh overdue list from server + updated savedMap
    try {
      const { data: cmpl } = await api.get("/shipments", { params: { status: "complete", limit: 500 } });
      const newOverdue = await recomputeOverdue(cmpl);
      setOverdue(newOverdue);
    } catch {}

    if (errors.length === 0) {
      toast.success(`Backed up ${done} file${done === 1 ? "" : "s"} across ${overdue.length} overdue shipment${overdue.length === 1 ? "" : "s"}`);
    } else {
      toast.error(`Saved ${done} of ${total}. ${errors.length} failed.`);
    }
    try { setHiccups7d(await getHiccupsSince(Date.now() - 7 * 86400000)); } catch {}
  };

  const retryFailed = async () => {
    if (!batch || batch.errors.length === 0) return;
    const failed = [...batch.errors];
    const total = failed.length;
    setBatch({ done: 0, total, current: null, errors: [], running: true });
    let done = 0;
    const newErrors = [];
    for (const task of failed) {
      setBatch({ done, total, current: { awb: task.awb, filename: task.filename }, errors: newErrors, running: true });
      try {
        const { data } = await withAutoRetry(
          () => api.get(`/documents/${task.docId}/download`, { responseType: "blob" }),
          {
            onRetry: ({ attempt }) => setBatch((b) => b && ({ ...b, current: { ...b.current, retrying: attempt } })),
            onRecovered: recordHiccupRecovery,
          },
        );
        await saveBlobToFolder(task.filename, data, task.awb);
        await markDocSaved(task.docId, { awb: task.awb });
        done += 1;
      } catch (e) {
        newErrors.push({ ...task, error: e.message || formatApiError(e) });
      }
    }
    setBatch({ done, total, current: null, errors: newErrors, running: false });

    // Refresh overdue list
    try {
      const { data: cmpl } = await api.get("/shipments", { params: { status: "complete", limit: 500 } });
      const newOverdue = await recomputeOverdue(cmpl);
      setOverdue(newOverdue);
    } catch {}

    if (newErrors.length === 0) {
      toast.success(`Retry successful — all ${done} file${done === 1 ? "" : "s"} saved`);
    } else {
      toast.error(`Retry saved ${done} of ${total}. ${newErrors.length} still failed.`);
    }
    try { setHiccups7d(await getHiccupsSince(Date.now() - 7 * 86400000)); } catch {}
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Overview</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">
            Command deck
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-lg">
            Track which shipments are missing documents at a glance.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/shipments/new" data-testid="quick-new-shipment-btn"
            className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150">
            <Plus size={16} /> New Shipment
          </Link>
          <Link to="/shipments" data-testid="quick-search-btn"
            className="bg-white text-zinc-900 border border-zinc-200 text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-zinc-100 transition-colors duration-150">
            <MagnifyingGlass size={16} /> Search
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 font-mono">Loading stats…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Shipments" value={stats?.total ?? 0} testid="stat-total" />
            <StatCard label="Complete" value={stats?.complete ?? 0} tone="success" testid="stat-complete" hint="all 5 docs uploaded" />
            <StatCard label="Incomplete" value={stats?.incomplete ?? 0} tone="warn" testid="stat-incomplete" hint="missing at least 1 doc" />
            <StatCard label="Completion Rate"
              value={stats?.total ? `${Math.round((stats.complete / stats.total) * 100)}%` : "—"}
              testid="stat-rate" />
          </div>

          {/* Backup Overdue panel — only when local folder is set & something is overdue */}
          {isFolderSupported() && folderName && overdue.length > 0 && (
            <div className="bg-white border-2 border-red-200 mb-6" data-testid="overdue-panel">
              <div className="px-5 py-4 border-b border-red-100 bg-red-50 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <FloppyDisk size={20} weight="duotone" className="text-red-700" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-mono font-bold">Backup Overdue · &gt;7 Days</div>
                    <div className="font-display font-black text-lg text-zinc-950 tracking-tight">
                      Complete shipments awaiting local backup
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-xs font-mono text-zinc-600">
                    {overdue.length} of {stats?.complete ?? 0} complete shipments · folder <span className="text-zinc-900">{folderName}</span>
                  </div>
                  <button
                    onClick={saveAllOverdue}
                    disabled={batch?.running}
                    data-testid="save-all-overdue-btn"
                    className="bg-red-700 text-white text-xs px-3 py-1.5 hover:bg-red-800 disabled:opacity-60 flex items-center gap-1 transition-colors duration-150"
                    title={`Save all ${overdue.length} overdue shipment(s) to ${folderName}`}
                  >
                    <DownloadSimple size={12} weight="bold" />
                    {batch?.running ? "Saving…" : "Save all overdue now"}
                  </button>
                </div>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">
                    <tr>
                      <th className="text-left px-5 py-2">AWB Number</th>
                      <th className="text-left px-5 py-2">Date</th>
                      <th className="text-left px-5 py-2">Age</th>
                      <th className="text-left px-5 py-2">Backup Progress</th>
                      <th className="text-right px-5 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdue.map((s) => (
                      <tr key={s.id} className="border-b border-zinc-100 hover:bg-red-50/50 transition-colors duration-150" data-testid={`overdue-row-${s.id}`}>
                        <td className="px-5 py-3 font-mono text-zinc-900 font-medium">
                          <Link to={`/shipments/${s.id}`} className="hover:text-red-700 hover:underline">{s.awb_number}</Link>
                        </td>
                        <td className="px-5 py-3 font-mono text-zinc-700">{s.doc_date || "—"}</td>
                        <td className="px-5 py-3">
                          <span className="inline-block px-2 py-0.5 text-[10px] font-mono border border-red-200 bg-red-50 text-red-700" data-testid={`overdue-age-${s.id}`}>
                            {s._days} days
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-1.5 bg-zinc-100 border border-zinc-200">
                              <div className="h-full bg-emerald-500" style={{ width: `${(s._saved / s._total) * 100}%` }} />
                            </div>
                            <span className="text-xs font-mono text-zinc-600">{s._saved}/{s._total}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <Link to={`/shipments/${s.id}`} data-testid={`overdue-save-${s.id}`}
                            className="inline-flex items-center gap-1 text-xs bg-red-700 text-white px-3 py-1.5 hover:bg-red-800 transition-colors duration-150">
                            <FloppyDisk size={12} weight="fill" /> Save now
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Incomplete shipments — prominent panel */}
          <div className="bg-white border-2 border-orange-200 mb-6" data-testid="incomplete-panel">
            <div className="px-5 py-4 border-b border-orange-100 bg-orange-50 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <ClockCountdown size={20} weight="duotone" className="text-[#FF4F00]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F00] font-mono font-bold">Action Required</div>
                  <div className="font-display font-black text-lg text-zinc-950 tracking-tight">
                    AWB numbers still awaiting documents
                  </div>
                </div>
              </div>
              <div className="text-xs font-mono text-zinc-600">
                {incomplete.length} shipment{incomplete.length === 1 ? "" : "s"} pending
              </div>
            </div>

            {incomplete.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle size={32} weight="duotone" className="text-emerald-500 mx-auto mb-2" />
                <div className="text-sm text-zinc-700 font-medium">All shipments are complete — nothing to file!</div>
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">
                    <tr>
                      <th className="text-left px-5 py-2">AWB Number</th>
                      <th className="text-left px-5 py-2">Date</th>
                      <th className="text-left px-5 py-2">Waiting</th>
                      <th className="text-left px-5 py-2">Consignee</th>
                      <th className="text-left px-5 py-2">Missing documents</th>
                      <th className="text-right px-5 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...incomplete]
                      .map((s) => {
                        const effective = s.doc_date || (s.created_at ? s.created_at.slice(0, 10) : "");
                        const days = effective
                          ? Math.max(0, Math.floor((Date.now() - new Date(effective).getTime()) / 86400000))
                          : 0;
                        return { ...s, _effective: effective, _days: days };
                      })
                      .sort((a, b) => {
                        // No date at all → push to bottom
                        if (!a._effective && !b._effective) return 0;
                        if (!a._effective) return 1;
                        if (!b._effective) return -1;
                        return a._effective.localeCompare(b._effective); // oldest first
                      })
                      .map((s) => {
                        const tone = s._days >= 7 ? "text-red-700 bg-red-50 border-red-200"
                          : s._days >= 3 ? "text-[#FF4F00] bg-orange-50 border-orange-200"
                          : "text-zinc-700 bg-zinc-100 border-zinc-200";
                        return (
                          <tr key={s.id} className="border-b border-zinc-100 hover:bg-orange-50/50 transition-colors duration-150" data-testid={`incomplete-row-${s.id}`}>
                            <td className="px-5 py-3 font-mono text-zinc-900 font-medium">
                              <Link to={`/shipments/${s.id}`} className="hover:text-[#002FA7] hover:underline">
                                {s.awb_number}
                              </Link>
                            </td>
                            <td className="px-5 py-3 font-mono text-zinc-700">{s.doc_date || "—"}</td>
                            <td className="px-5 py-3">
                              <span className={`inline-block px-2 py-0.5 text-[10px] font-mono border ${tone}`} data-testid={`waiting-${s.id}`}>
                                {s._effective ? `${s._days} day${s._days === 1 ? "" : "s"}` : "—"}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-zinc-700 truncate max-w-[180px]">{s.consignee || "—"}</td>
                            <td className="px-5 py-3">
                              <div className="flex flex-wrap gap-1">
                                {s.docs_missing.map((t) => (
                                  <span key={t} className={`inline-block px-2 py-0.5 text-[10px] font-mono border ${DOC_TYPE_COLORS[t]}`}>
                                    {DOC_TYPE_LABELS[t]}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-5 py-3 text-right whitespace-nowrap">
                              <Link to={`/shipments/${s.id}`} data-testid={`upload-cta-${s.id}`}
                                className="inline-flex items-center gap-1 text-xs bg-[#002FA7] text-white px-3 py-1.5 hover:bg-[#00227A] transition-colors duration-150">
                                Upload <ArrowRight size={12} />
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Missing by type */}
            <div className="lg:col-span-1 bg-white border border-zinc-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Missing by document</div>
                <WarningCircle size={14} className="text-[#FF4F00]" />
              </div>
              <div className="space-y-2">
                {REQUIRED_DOC_TYPES.map((t) => {
                  const n = stats?.missing_by_type?.[t] ?? 0;
                  return (
                    <div key={t} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0">
                      <span className={`inline-block px-2 py-0.5 text-[11px] font-mono border ${DOC_TYPE_COLORS[t]}`}>{DOC_TYPE_LABELS[t]}</span>
                      <span className={`font-mono text-sm ${n > 0 ? "text-[#FF4F00]" : "text-zinc-400"}`} data-testid={`missing-${t}`}>
                        {n > 0 ? `${n} shipment${n === 1 ? "" : "s"}` : "✓ all filed"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent */}
            <div className="lg:col-span-2 bg-white border border-zinc-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Recent shipments</div>
                <Link to="/shipments" className="text-xs text-[#002FA7] hover:underline flex items-center gap-1">
                  View all <ArrowRight size={12} />
                </Link>
              </div>
              {recent.length === 0 ? (
                <div className="py-10 text-center text-sm text-zinc-500 font-mono">
                  <Package size={32} weight="duotone" className="mx-auto text-zinc-300 mb-2" />
                  No shipments yet. <Link to="/shipments/new" className="text-[#002FA7] hover:underline">Create the first one →</Link>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">AWB</th>
                      <th className="text-left py-2">Date</th>
                      <th className="text-left py-2 hidden md:table-cell">Consignee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((s) => (
                      <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`recent-row-${s.id}`}>
                        <td className="py-2">
                          {s.is_complete ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-emerald-200 bg-emerald-50 text-emerald-800 px-2 py-0.5">
                              <CheckCircle size={10} weight="fill" /> OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-orange-200 bg-orange-50 text-[#FF4F00] px-2 py-0.5">
                              <WarningCircle size={10} weight="fill" /> {s.docs_missing.length}
                            </span>
                          )}
                        </td>
                        <td className="py-2 font-mono text-zinc-900">
                          <Link to={`/shipments/${s.id}`} className="hover:underline">{s.awb_number}</Link>
                        </td>
                        <td className="py-2 font-mono text-zinc-700">{s.doc_date || "—"}</td>
                        <td className="py-2 hidden md:table-cell text-zinc-600">{s.consignee || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {hiccups7d > 0 && (
        <div className="mt-8 pt-4 border-t border-zinc-200 flex items-center justify-center gap-2 text-xs text-zinc-500 font-mono" data-testid="hiccup-footer">
          <span className="inline-flex items-center justify-center w-5 h-5 bg-emerald-100 text-emerald-700 border border-emerald-200">
            <CheckCircle size={12} weight="fill" />
          </span>
          Auto-recovered <span className="text-zinc-900" data-testid="hiccup-count">{hiccups7d}</span> network hiccup{hiccups7d === 1 ? "" : "s"} this week
        </div>
      )}

      {batch && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="batch-progress">
          <div className="bg-white border border-zinc-200 shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-red-700 text-white flex items-center justify-center">
                <DownloadSimple size={18} weight="bold" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-black text-lg text-zinc-950 tracking-tight leading-tight">Backing up overdue shipments</div>
                <div className="text-xs text-zinc-500 font-mono truncate">{folderName}</div>
              </div>
            </div>
            <div className="text-sm text-zinc-700 mb-2">
              {batch.done} of {batch.total} files saved
            </div>
            <div className="w-full h-2 bg-zinc-100 border border-zinc-200 mb-3">
              <div className="h-full bg-red-600 transition-all duration-200"
                style={{ width: batch.total ? `${(batch.done / batch.total) * 100}%` : "0%" }} />
            </div>
            {batch.current && (
              <div className="text-xs font-mono text-zinc-500 truncate">
                → <span className="text-zinc-700">{batch.current.awb}</span> · {batch.current.filename}
                {batch.current.retrying && (
                  <span className="ml-2 text-amber-600" data-testid="retry-indicator">
                    (retry {batch.current.retrying})
                  </span>
                )}
              </div>
            )}
            {batch.errors.length > 0 && (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 max-h-32 overflow-y-auto" data-testid="batch-errors">
                {batch.errors.map((e, i) => (
                  <div key={i}>• [{e.awb}] {e.filename}: {e.error}</div>
                ))}
              </div>
            )}
            {!batch.running && (
              <div className="mt-4 flex justify-end gap-2">
                {batch.errors.length > 0 && (
                  <button onClick={retryFailed} data-testid="batch-retry-btn"
                    className="px-3 py-2 text-sm bg-red-700 text-white hover:bg-red-800 transition-colors duration-150 flex items-center gap-1">
                    <DownloadSimple size={12} weight="bold" /> Retry failed ({batch.errors.length})
                  </button>
                )}
                <button onClick={() => setBatch(null)} data-testid="batch-close-btn"
                  className="px-3 py-2 text-sm border border-zinc-200 bg-white hover:bg-zinc-100 transition-colors duration-150 flex items-center gap-1">
                  <X size={12} /> Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
